import { createMemo, Show } from "solid-js";
import type { Status } from "../../types";

type ProgressBarProps = {
  variant: "stepped" | "determinate";
  label: string;
  status: Status;
  current: number;
  max: number;
  backgroundColor: "default" | "tertiary";
};

const ProgressBar = (props: ProgressBarProps) => {
  const current = createMemo(() => ({
    value: props.current,
    percentage: toProgressPercentage(props.current, props.max),
  }));

  return (
    <div class="flex flex-col text-small-medium text-primary">
      <div class="flex items-center text-small-medium" classList={{ "mb-1": props.variant === "stepped" }}>
        <span class="truncate">{props.label}</span>
        <Show when={props.variant === "stepped"}>
          <span class="ml-auto">{`${current().value}/${props.max}`}</span>
        </Show>
      </div>
      <div
        role="progressbar"
        aria-label={props.variant === "determinate" ? `${current().percentage}%` : `${current().value} of ${props.max}`}
        aria-valuenow={current().value}
        aria-valuemin={0}
        aria-valuemax={props.max}
        class="flex items-center gap-x-2"
        classList={{ "h-4": props.variant === "determinate" }}
      >
        <div
          class="h-1.5 w-full rounded-sm"
          classList={{
            "bg-surface-default": props.backgroundColor === "default",
            "bg-surface-subtle": props.backgroundColor === "tertiary",
          }}
        >
          <div
            class="h-1.5 rounded-sm"
            classList={{
              "bg-surface-status-upgrade": props.status === "upgrade",
              "bg-surface-status-informative": props.status === "informative",
              "bg-surface-status-warning": props.status === "warning",
              "bg-surface-status-danger": props.status === "danger",
            }}
            style={{
              width: `${current().percentage}%`,
            }}
          />
        </div>
        <Show when={props.variant === "determinate"}>
          <span class="ml-auto">{`${current().percentage}%`}</span>
        </Show>
      </div>
    </div>
  );
};

export default ProgressBar;

const toProgressPercentage = (current: number, total: number) => {
  const percentage = Math.floor((current * 100) / total);
  if (!isFinite(percentage)) {
    return 0;
  }
  return Math.min(percentage, 100);
};
